import { DateTime } from 'luxon';

import { currentPreferences } from '@/stores/preferences-store';

/**
 * Every number, date and amount the console renders passes through here.
 *
 * A component calling `toLocaleString` directly is a component that formats in the
 * browser's locale and the browser's timezone, which is wrong for a church whose
 * administrator is travelling and wrong for every locale but the developer's.
 *
 * Reference: documentation/frontend-design.md §4 and §5.
 */

/** ISO 4217 exponents that differ from the default of 2. Mirrors the server's list. */
const EXPONENTS: Record<string, number> = {
  BHD: 3, BIF: 0, CLF: 4, CLP: 0, DJF: 0, GNF: 0, IQD: 3, ISK: 0, JOD: 3, JPY: 0,
  KMF: 0, KRW: 0, KWD: 3, LYD: 3, OMR: 3, PYG: 0, RWF: 0, TND: 3, UGX: 0, UYI: 0,
  UYW: 4, VND: 0, VUV: 0, XAF: 0, XOF: 0, XPF: 0,
};

export const decimalPlaces = (currency: string): number =>
  EXPONENTS[currency.toUpperCase()] ?? 2;

/**
 * Integer minor units to a formatted amount.
 *
 * Negatives are parenthesised as well as coloured, because the sign has to survive
 * a black-and-white printout of a report.
 */
export const formatMoney = (
  minorUnits: number,
  currency: string,
  options: { locale?: string; showSymbol?: boolean; parenthesiseNegative?: boolean } = {},
): string => {
  const locale = options.locale ?? currentPreferences().locale;
  const places = decimalPlaces(currency);
  const negative = minorUnits < 0;
  const magnitude = Math.abs(minorUnits) / 10 ** places;

  const formatted = new Intl.NumberFormat(locale, {
    style: options.showSymbol === false ? 'decimal' : 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: places,
    maximumFractionDigits: places,
  }).format(magnitude);

  if (!negative) return formatted;
  return options.parenthesiseNegative === false ? `-${formatted}` : `(${formatted})`;
};

/** Axis ticks: compact so “GHS 15,000.00” does not eat the plot. Hover still uses `formatMoney`. */
export const formatCompactMoney = (
  minorUnits: number,
  currency: string,
  options: { locale?: string } = {},
): string => {
  const locale = options.locale ?? currentPreferences().locale;
  const places = decimalPlaces(currency);
  const magnitude = minorUnits / 10 ** places;

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(magnitude);
};

/** Major-unit text as typed by a user, converted to the integer we send. */
export const toMinorUnits = (amount: string, currency: string): number | null => {
  const places = decimalPlaces(currency);
  const cleaned = amount.trim();
  const match = /^(-)?(\d+)(?:[.,](\d+))?$/.exec(cleaned);
  if (!match) return null;

  const [, sign, whole, fraction = ''] = match;
  if (fraction.length > places) return null;

  const minor = Number(`${whole}${fraction.padEnd(places, '0')}`);
  return sign ? -minor : minor;
};

/** The integer we hold, shown in the input as the user would type it. */
export const toMajorUnits = (minorUnits: number, currency: string): string => {
  const places = decimalPlaces(currency);
  const negative = minorUnits < 0;
  const digits = Math.abs(minorUnits).toString().padStart(places + 1, '0');
  const whole = digits.slice(0, digits.length - places);
  const fraction = places === 0 ? '' : `.${digits.slice(digits.length - places)}`;
  return `${negative ? '-' : ''}${whole}${fraction}`;
};

export const formatNumber = (value: number, locale?: string): string =>
  new Intl.NumberFormat(locale ?? currentPreferences().locale).format(value);

export const formatPercent = (fraction: number, locale?: string): string =>
  new Intl.NumberFormat(locale ?? currentPreferences().locale, {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(fraction);

/**
 * A date-only value. Never acquires a time component and never converts, because a
 * contribution dated the 9th is the 9th wherever it is read.
 */
export const formatDate = (
  isoDate: string,
  options: { locale?: string; style?: 'short' | 'medium' | 'long' } = {},
): string => {
  const { locale } = { ...currentPreferences(), ...options };
  const parsed = DateTime.fromISO(isoDate, { zone: 'utc' });
  if (!parsed.isValid) return '—';

  const format = {
    short: DateTime.DATE_SHORT,
    medium: DateTime.DATE_MED,
    long: DateTime.DATE_FULL,
  }[options.style ?? 'medium'];

  return parsed.setLocale(locale).toLocaleString(format);
};

/** A church-story date. Year and month precisions must not display as 1 January. */
export const formatChronicleDate = (
  occurredOn: string,
  precision: 'day' | 'month' | 'year',
  options: { locale?: string } = {},
): string => {
  if (precision === 'year') return occurredOn.slice(0, 4);
  const { locale } = { ...currentPreferences(), ...options };
  const parsed = DateTime.fromISO(occurredOn, { zone: 'utc' });
  if (!parsed.isValid) return '—';
  if (precision === 'month') return parsed.setLocale(locale).toFormat('LLLL yyyy');
  return formatDate(occurredOn, { locale, style: 'medium' });
};

export const chronicleMonthLabel = (month: number, locale?: string): string => {
  const parsed = DateTime.fromObject({ year: 2000, month }, { zone: 'utc' });
  if (!parsed.isValid) return String(month);
  return parsed.setLocale(locale ?? currentPreferences().locale).toFormat('LLLL');
};

/** Axis label for a giving-trend bucket. Grain comes from the series, not the date string. */
export const formatTrendDate = (
  isoDate: string,
  grain: 'day' | 'week' | 'month' = 'day',
  options: { locale?: string } = {},
): string => {
  const locale = options.locale ?? currentPreferences().locale;
  const parsed = DateTime.fromISO(isoDate, { zone: 'utc' }).setLocale(locale);
  if (!parsed.isValid) return '—';
  if (grain === 'month') return parsed.toFormat('MMM yyyy');
  return parsed.toFormat('MMM d');
};

/** An instant, read in the church's timezone. */
export const formatDateTime = (
  isoTimestamp: string,
  options: { locale?: string; timezone?: string; withZone?: boolean } = {},
): string => {
  const { locale, timezone } = { ...currentPreferences(), ...options };
  const parsed = DateTime.fromISO(isoTimestamp, { zone: 'utc' }).setZone(timezone);
  if (!parsed.isValid) return '—';

  const text = parsed.setLocale(locale).toLocaleString(DateTime.DATETIME_MED);
  return options.withZone ? `${text} (${parsed.offsetNameShort})` : text;
};

export const formatTime = (
  isoTimestamp: string,
  options: { locale?: string; timezone?: string } = {},
): string => {
  const { locale, timezone } = { ...currentPreferences(), ...options };
  const parsed = DateTime.fromISO(isoTimestamp, { zone: 'utc' }).setZone(timezone);
  return parsed.isValid ? parsed.setLocale(locale).toLocaleString(DateTime.TIME_SIMPLE) : '—';
};

/**
 * "2 hours ago" for recency, the absolute value beyond a week.
 *
 * Relative time past about a week stops helping — nobody reads "37 days ago" and
 * knows the date — so it switches rather than getting less useful.
 */
export const formatRelative = (
  isoTimestamp: string,
  options: { locale?: string; timezone?: string } = {},
): string => {
  const { locale, timezone } = { ...currentPreferences(), ...options };
  const parsed = DateTime.fromISO(isoTimestamp, { zone: 'utc' }).setZone(timezone);
  if (!parsed.isValid) return '—';

  const days = Math.abs(parsed.diffNow('days').days);
  if (days > 7) return formatDateTime(isoTimestamp, options);

  return parsed.setLocale(locale).toRelative() ?? formatDateTime(isoTimestamp, options);
};

/** The timezone label shown beside a field that authors a scheduled time. */
export const timezoneLabel = (timezone?: string): string => {
  const zone = timezone ?? currentPreferences().timezone;
  const now = DateTime.now().setZone(zone);
  return `${zone.replace(/_/g, ' ')} · ${now.toFormat('HH:mm')}`;
};

/** Today, as the church reads it. Used as a default in date pickers. */
export const churchToday = (timezone?: string): string =>
  DateTime.now()
    .setZone(timezone ?? currentPreferences().timezone)
    .toFormat('yyyy-MM-dd');

const GIB = 1024 ** 3;
const MIB = 1024 ** 2;

/** Whole-plan storage display: prefer GB, fall back to MB for smaller usage. */
export const storageDisplayParts = (
  bytes: number,
): { size: number; unit: 'gb' | 'mb' } => {
  if (bytes >= GIB) {
    return { size: Math.round((bytes / GIB) * 10) / 10, unit: 'gb' };
  }
  return { size: Math.max(0, Math.round((bytes / MIB) * 10) / 10), unit: 'mb' };
};
